import { useMemo } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { MarketingTag, MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { EntityRecord } from "@/types/entity";
import { useEntityMarketingAssets } from "../api/marketingAssets.api";
import { ENTITY_RECORD_FIELD_TO_API } from "../api/marketingAssets.query-keys";
import type { SwivuMarketingAsset } from "../api/marketingAssets.types";

interface TemporalGroup {
  tag: MarketingTag;
  assets: SwivuMarketingAsset[];
}

interface FolderGroup {
  tag: MarketingTag;
  assets: SwivuMarketingAsset[];
}

export interface MarketingAssetsData {
  assets: SwivuMarketingAsset[];
  temporalGroups: TemporalGroup[];
  folderGroups: FolderGroup[];
  totalApproved: number;
  allTags: MarketingTag[];
  isLoading: boolean;
}

export const useMarketingAssets = (entityRecord: EntityRecord): MarketingAssetsData => {
  const { project, selectedBoard } = useSwivuBryntumContext();

  const { version: tagVersion } = useSwivuStore<MarketingTag>({ storeId: STORE_KEYS.marketingTagStore });

  const entityType = ENTITY_RECORD_FIELD_TO_API[entityRecord.entityType];
  const boardId = selectedBoard?.id ?? 0;
  const entityId = Number(entityRecord.id);

  const { assets, isLoading } = useEntityMarketingAssets({
    boardId,
    // Placeholder only — query is disabled when entityType is missing.
    entityType: entityType ?? "task",
    entityId,
    enabled: !!boardId && !!entityType && Number.isFinite(entityId),
  });

  return useMemo(() => {
    if (!project || !entityType) {
      return {
        assets: [],
        temporalGroups: [],
        folderGroups: [],
        totalApproved: 0,
        allTags: [],
        isLoading: false,
      };
    }

    const allTags = (project.marketingTagStore?.query(() => true, true) ?? []) as MarketingTag[];

    const temporalTags = allTags.filter((t) => t.type === MarketingTagType.TEMPORAL);
    const folderTags = allTags.filter((t) => t.type === MarketingTagType.CONTENT || t.isFolderEligible);

    const temporalGroups: TemporalGroup[] = temporalTags
      .map((tag) => ({
        tag,
        assets: assets.filter((a) => a.tags?.includes(tag.id)),
      }))
      .filter((g) => g.assets.length > 0);

    const folderGroups: FolderGroup[] = folderTags
      .map((tag) => ({
        tag,
        assets: assets.filter((a) => a.tags?.includes(tag.id)),
      }))
      .filter((g) => g.assets.length > 0);

    return {
      assets,
      temporalGroups,
      folderGroups,
      totalApproved: assets.length,
      allTags,
      isLoading,
    };
  }, [project, entityType, tagVersion, assets, isLoading]);
};
